import { useContext } from 'react'
import { useFormContext } from 'react-hook-form'

import { PageVariablesContext } from '@components/Variables'
import { VariableValue } from '@components/Variables/types'

type useHandlersProps = {
  index?: number
  variables?: VariableValue[]
  initialVariables?: VariableValue[]
  removeVariable?: (index: number) => void
}

export const useHandlers = ({
  index,
  variables,
  removeVariable,
  initialVariables,
}: useHandlersProps) => {
  const { onCreateOrEdit, isCreateOrEdit } = useContext(PageVariablesContext)

  const { watch, handleSubmit, setValue } = useFormContext()

  const internalIdInputName = `variables.${index}.internalId`
  const titleInputName = `variables.${index}.title`
  const variableTypeInputName = `variables.${index}.variableType`
  const valueTypeInputName = `variables.${index}.valueType`
  const isEditInputName = `variables.${index}.isEdit`

  const watchInternalId = watch(internalIdInputName)
  const watchTitle = watch(titleInputName)
  const watchVariableType = watch(variableTypeInputName)
  const watchValueType = watch(valueTypeInputName)
  const isEditVariable = watch(isEditInputName)

  const activeVariable =
    variables && index != null ? (variables[index] as VariableValue) : ({} as VariableValue)

  const handleEditVariable = (edit: boolean) => {
    setValue(isEditInputName, edit)
    onCreateOrEdit?.(edit)
  }

  const handleResetFields = () => {
    if (variables && variables.length > 0) {
      const arrayVariablesWithRestoredValues = variables.map(variable => {
        const variableInitial = initialVariables?.find(
          variableFind => variableFind.id === variable.id
        )

        const resetVariable = variableInitial || variable

        return {
          ...variable,
          title: resetVariable.title,
          variableType: { id: resetVariable.variableType, label: resetVariable.variableType },
          valueType: { id: resetVariable.valueType, label: resetVariable.valueType },
          isEdit: false,
        }
      })

      setValue('variables', arrayVariablesWithRestoredValues)
    }

    if (!isCreateOrEdit && typeof index === 'number') {
      removeVariable?.(index)

      return
    }

    handleEditVariable(false)
  }

  const isFormValid =
    watchInternalId?.trim() && watchTitle?.trim() && watchVariableType && watchValueType

  return {
    data: {
      internalIdInputName,
      titleInputName,
      valueTypeInputName,
      variableTypeInputName,
      watchValueType,
      watchVariableType,
      watchTitle,
      watchInternalId,
      activeVariable,
      isEditVariable,
      isFormValid,
      isCreateOrEdit,
    },
    handlers: { handleEditVariable, handleResetFields, handleSubmit },
  }
}
